/** Internal workspace hostname desk — one build, subdomain selects IA focus. */
export type WorkspaceDesk =
  | 'admin'
  | 'app'
  | 'onboarding'
  | 'reception'
  | 'recruiter'
  | 'hr'
  | 'pro'
  | 'finance'
  | 'sales'
  | 'operations'
  | 'client'
  | 'supervisor'

const WORKSPACE_DESK_HOSTS: readonly WorkspaceDesk[] = [
  'admin',
  'app',
  'onboarding',
  'reception',
  'recruiter',
  'hr',
  'pro',
  'finance',
  'sales',
  'operations',
  'client',
  'supervisor',
]

const DEV_DESK_OVERRIDE = import.meta.env.VITE_WORKSPACE_DESK as string | undefined

function isWorkspaceDesk(value: string): value is WorkspaceDesk {
  return (WORKSPACE_DESK_HOSTS as readonly string[]).includes(value)
}

/**
 * Desk sent to the API. PRO routes stay on the PRO desk even when opened from another host,
 * so own-visa staff stay off those screens.
 */
export function resolveApiWorkspaceDesk(host: string, pathname: string): WorkspaceDesk | null {
  if (pathname === '/pro' || pathname.startsWith('/pro/')) return 'pro'
  return resolveWorkspaceDeskFromHost(host)
}

/** First label of `onboarding.amglobal.co` → `onboarding`. Local dev returns null unless `VITE_WORKSPACE_DESK` is set. */
export function resolveWorkspaceDeskFromHost(host: string): WorkspaceDesk | null {
  // Vitest often inherits a local VITE_WORKSPACE_DESK; do not let it mask host→desk mapping tests.
  if (
    import.meta.env.DEV &&
    import.meta.env.MODE !== 'test' &&
    DEV_DESK_OVERRIDE &&
    isWorkspaceDesk(DEV_DESK_OVERRIDE.trim())
  ) {
    return DEV_DESK_OVERRIDE.trim() as WorkspaceDesk
  }

  const normalized = host.split(':')[0]?.trim().toLowerCase() ?? ''
  if (!normalized || normalized === 'localhost' || normalized === '127.0.0.1') {
    return null
  }

  const sub = normalized.split('.')[0] ?? ''
  return isWorkspaceDesk(sub) ? sub : null
}

/**
 * Team attendance Period / Overtime views (supervisor subdomain UX).
 * On localhost, use `VITE_WORKSPACE_DESK=app` for admin + supervisor in one session.
 */
export function isSupervisorAttendanceDesk(desk: WorkspaceDesk | null): boolean {
  if (desk === 'supervisor' || desk === 'app') return true
  if (import.meta.env.DEV && (desk === null || desk === 'admin')) return true
  return false
}
